import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, AccountCircle } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import buddyMascot from '../assets/freepik__the-pant-color-should-be-light-yellow__49787 (1) (1).png';

const StudentLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "http://";
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
        const trimmedEmail = email.trim().toLowerCase();
        const payload = {
          institutional_email: trimmedEmail,
          password: password,
          bot_id: process.env.REACT_APP_BOT_ID || "c36c2605-6e79-47b2-b22e-1ea5b6381b84",
          bot_name: process.env.REACT_APP_BOT_NAME || "SSODL Buddy"
        };
        const response = await axios.post(`${API_URL}/auth/student-login`, payload);
        const { token, student, session_id } = response.data;

        localStorage.setItem('studentToken', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Build query string with actual student information
        const queryParams = new URLSearchParams({
          id: student.id,
          prn: student.prn,
          name: student.name,
          email: student.email,
          phone: student.phone,
          course: student.course,
          session_id: session_id
        });

        navigate(`/student/dashboard?${queryParams.toString()}`);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Login failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('Please enter your institutional email first');
      return;
    }

    setForgotLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/student-forgot-password`, {
        institutional_email: trimmedEmail
      });
      setInfo(response.data?.message || 'Password reset request sent');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to send forgot password request';
      setError(message);
    } finally {
      setForgotLoading(false);
    }
  };



  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #c52f33 0%, #8b1f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 1)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {/* Buddy Mascot */}
            <Box sx={{ mb: 2 }}>
              <img 
                src={buddyMascot}
                alt="SSODL Buddy Mascot" 
                style={{ height: '120px', display: 'block', margin: '0 auto 20px' }}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <img 
                src="https://assets.getalabs.com/assets/icons/ssodl_logo.png" 
                alt="SSODL Logo" 
                style={{ height: '80px', display: 'block', margin: '0 auto' }}
              />
            </Box>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
              Buddy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Access your academic dashboard
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {info && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {info}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Institutional Email Address"
              placeholder="PRN@slms.ssodl.edu.in"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#c52f33' },
                  '&.Mui-focused fieldset': { borderColor: '#c52f33' }
                }
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#c52f33' },
                  '&.Mui-focused fieldset': { borderColor: '#c52f33' }
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !email || !password}
              sx={{
                mt: 2,
                mb: 2,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #c52f33 0%, #8b1f23 100%)',
                color: '#ffffff !important',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #b02a2e 0%, #7a1b1f 100%)',
                  color: '#ffffff !important'
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.5) !important'
                }
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
            <Button
              fullWidth
              variant="text"
              disabled={forgotLoading}
              onClick={handleForgotPassword}
              sx={{ mt: 1 }}
            >
              {forgotLoading ? 'Sending...' : 'Forgot Password'}
            </Button>
          </Box>

        </Paper>
      </Container>
    </Box>
  );
};

export default StudentLogin;